<template>
  <v-container fluid class="pa-0 ma-0">
    <v-card elevation="0" class="pa-0 ma-0">
      <v-card-title>
        <v-icon>mdi-poll</v-icon>&nbsp;
        {{ $t('queues.reports.title') }}
      </v-card-title>
      <v-expansion-panels accordion flat v-model="filterPanel">
        <v-expansion-panel>
          <v-expansion-panel-header class="pt-0">
            <template v-slot:default="{ open }">
              <v-row no-gutters>
                <v-col :cols="isMobile ? 3 : 1"
                  :offset="isMobile? 9 : 11"
                  class="text--secondary d-flex justify-end">
                  <v-fade-transition leave-absolute>
                    <span class="text-caption">
                      {{ open ? $t('queues.reports.filters.hideFilters') : $t('queues.reports.filters.showFilters') }}
                    </span>
                  </v-fade-transition>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ReportFilters @refreshData="refreshData" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div v-if="loaded">
        <Breadcrumbs :breadcrumbs="breadcrumbs" @refreshData="refreshData" />
        <QueueSummary v-if="reportType === 'summary'" :report-rows="queueSummary" :selected-queues="selectedQueues" :hourly="hourly" :scope="scope" @refreshData="refreshData" />
        <CallDetails v-if="reportType === 'callDetails'" :report-rows="callDetails" :selected-queues="selectedQueues" :scope="scope" :startDate="startDate" :endDate="endDate" :timezone="timezone" @refreshData="refreshData" @playRecording="playRecording" />
        <AgentDetails v-if="reportType === 'agentDetails'" :report-rows="agentDetails" :scope="scope" :startDate="startDate" :endDate="endDate" :timezone="timezone" @refreshData="refreshData" @playRecording="playRecording" />
      </div>
    </v-card>
    <Player :show="showPlayer" :clid="playerClid"
      :date="playerDate" :duration="playerDur"
      :src="recSrc" @player-close="showPlayer = false" />
  </v-container>
</template>

<script>
import API from '@/api'
import { mapGetters, mapActions } from 'vuex'
import events from 'Store/events'
import ReportFilters from './filters'
import Breadcrumbs from './breadcrumbs'
import QueueSummary from './queue_summary'
import CallDetails from './call_details'
import AgentDetails from './agent_details'
import CDR from 'Store/class/cdr'
import Player from '../../cdrs/player'

export default {
  name: 'queues-reports',
  components: { ReportFilters, Breadcrumbs, QueueSummary, CallDetails, AgentDetails, Player },
  data () {
    return {
      filterPanel: 0,
      loaded: false,
      reportType: null,
      selectedQueues: [],
      selectedAgent: null,
      hourly: false,
      scope: null,
      startDate: null,
      endDate: null,
      timezone: null,
      breadcrumbs: [],
      // player data
      showPlayer: false,
      playerClid: null,
      playerDate: null,
      playerDur: null,
      recSrc: null
    }
  },
  computed: {
    ...mapGetters({
      queueSummary: 'queues/queueSummary',
      callDetails: 'queues/callDetails',
      agentDetails: 'queues/agentDetails'
    }),
    isMobile () {
      return this.$vuetify.breakpoint.mobile
    }
  },
  methods: {
    ...mapActions({
      loadReport: 'queues/loadReportType'
    }),
    refreshData (data) {
      events.mask(this.$t('queues.reports.loading'))
      this.reportType = data.reportType
      this.selectedQueues = data.queue ?? this.selectedQueues
      this.hourly = data.hourly ?? this.hourly
      this.scope = data.scope ?? this.scope
      this.startDate = data.startDate ?? this.startDate
      this.endDate = data.endDate ?? this.endDate
      this.timezone = data.timezone ?? this.timezone

      this.loadReport({
        reportType: data.reportType,
        queues: this.selectedQueues,
        agentExtension: data.agentExtension,
        startDate: data.startDate,
        endDate: data.endDate,
        hourly: this.hourly,
        timezone: this.timezone,
        limit: data.limit ?? 25,
        offset: data.offset ?? 0
      }).catch((err) => {
        this.dbg(err.response)
        events.pubError({ key: 'queues.reports.loadFail' })
      }).finally(() => {
        this.loaded = true
        events.unmask()
      })

      if (data.breadcrumb_op === 'new') {
        this.breadcrumbs = [data]
      } else if (data.breadcrumb_op === 'add') {
        this.breadcrumbs.push(data)
      } else if (data.breadcrumb_op === 'back') {
        this.breadcrumbs.splice(data.breadcrumb_idx + 1)
      }
      if (this.isMobile) this.filterPanel = null
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    playRecording (cdrId) {
      events.mask(this.$t('cdrs.loading'))
      let cdrObj
      API.getCDR(cdrId)
        .then(cdr => {
          cdrObj = new CDR(cdr.data)
          return API.getCDRRec(cdrId)
        }).then(rec => {
          if (!rec || !rec.data) return
          if (!cdrObj) return
          this.recSrc = URL.createObjectURL(rec.data)
          this.playerClid = cdrObj.fromDisplay
          this.playerDate = new Date(cdrObj.calldate).getTime()
          this.playerDur = cdrObj.duration
          this.showPlayer = true
        }).catch(err => {
          this.dbg(err.response)
          events.pubError({ key: 'cdrs.err.recNotFound' })
        }).finally(() => {
          events.unmask()
        })
    }
  }
}
</script>

<style>
.v-data-table.report-table > .v-data-table__wrapper > table > tbody > tr > th, .v-data-table > .v-data-table__wrapper > table > thead > tr > th, .v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
  font-size: 0.9rem;
}
.v-data-table.report-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
  border-bottom-width: medium;
}
.v-data-table.report-table table {
  border-spacing: 2px 0;
}
.v-data-table.report-table th.bordered {
  border-width: thin;
  border-style: solid;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.v-data-table.report-table td.bordered {
  border-left-width: thin;
  border-left-style: solid;
  border-right-width: thin;
  border-right-style: solid;
}
.v-data-table.report-table .totals td.bordered {
  border-style: solid;
  border-width: thin;
  border-top: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
.v-data-table.report-table .abandoned {
  border-color: #F6BEBD;
}
.v-data-table.report-table .answered {
  border-color: #B2D7F4;
}
.v-data-table.report-table .exit {
  border-color: #90A4AE;
}
.v-data-table.report-table .timeout {
  border-color: #E9E9E9;
}
.theme--light.v-data-table.report-table tbody tr:nth-of-type(even):not(.v-row-group__header) {
  background-color: rgba(0, 0, 0, .03);
}
.theme--dark.v-data-table.report-table tbody tr:nth-of-type(even):not(.v-row-group__header) {
  background-color: rgba(0, 0, 0, .5);
}
.v-card.table-card {
  max-width: 1200px;
  margin: auto;
}
.report-table th.text-center.sortable > i {
  position: absolute;
}
</style>
